import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import type { MemberOrder, MemberProfile, ProductView } from '../types';
import { readJson, writeJson } from '../lib/storage';

const runtime = window.JAIME_RUNTIME_CONFIG || {};
const url = (import.meta.env.VITE_SUPABASE_URL || runtime.supabaseUrl || '').trim();
const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || runtime.supabaseAnonKey || '').trim();
const production = Boolean(url && anonKey && /^https:\/\//i.test(url));
const supabase: SupabaseClient | null = production ? createClient(url!, anonKey!, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storageKey: 'jaime-membership-auth-v49' }
}) : null;

const PROFILE_KEY = 'jaime-demo-member-profile-v49';
const SESSION_KEY = 'jaime-demo-member-session-v49';
const ORDERS_KEY = 'jaime-demo-member-orders-v49';
const VIEWS_KEY = 'jaime-recent-product-views-v49';
const PENDING_KEY = 'jaime-pending-registration-v49';
export const DEMO_CODE = '246810';

type Registration = { fullName: string; phone: string; email: string; birthdayMonth: number; marketingConsent: boolean };

interface AuthValue {
  previewMode: boolean;
  loading: boolean;
  sessionEmail: string | null;
  profile: MemberProfile | null;
  orders: MemberOrder[];
  views: ProductView[];
  sendSignInCode: (email: string) => Promise<void>;
  sendRegistrationCode: (registration: Registration) => Promise<void>;
  verifyCode: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  recordView: (view: Omit<ProductView, 'viewed_at'> & { viewed_at?: string }) => Promise<void>;
  lookupMember: (memberNumber: string) => Promise<{ profile: MemberProfile; orders: MemberOrder[] } | null>;
  isStaff: () => Promise<boolean>;
  addOrder: (memberId: string, order: Omit<MemberOrder, 'id' | 'member_id' | 'status'>) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function demoProfile(): MemberProfile | null { return readJson<MemberProfile | null>(PROFILE_KEY, null); }
function demoOrders(): MemberOrder[] { return readJson<MemberOrder[]>(ORDERS_KEY, []); }
function demoViews(): ProductView[] { return readJson<ProductView[]>(VIEWS_KEY, []); }

function makeMemberNumber() {
  const seed = `${Date.now()}${Math.floor(Math.random() * 10000)}`.slice(-8);
  return `JM-${seed}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [demoEmail, setDemoEmail] = useState<string | null>(() => localStorage.getItem(SESSION_KEY));
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [orders, setOrders] = useState<MemberOrder[]>([]);
  const [views, setViews] = useState<ProductView[]>([]);

  const loadData = useCallback(async (activeSession?: Session | null) => {
    if (!supabase) {
      const email = localStorage.getItem(SESSION_KEY);
      const p = demoProfile();
      setDemoEmail(email);
      setProfile(email && p?.email.toLowerCase() === email.toLowerCase() ? p : null);
      setOrders(email ? demoOrders() : []);
      setViews(email ? demoViews() : []);
      setLoading(false);
      return;
    }
    const s = activeSession === undefined ? (await supabase.auth.getSession()).data.session : activeSession;
    setSession(s);
    if (!s?.user) { setProfile(null); setOrders([]); setViews([]); setLoading(false); return; }
    const [p, o, v] = await Promise.all([
      supabase.from('member_profiles').select('*').eq('id', s.user.id).maybeSingle(),
      supabase.from('orders').select('*').eq('member_id', s.user.id).order('purchased_at', { ascending: false }),
      supabase.from('product_views').select('*').eq('member_id', s.user.id).order('viewed_at', { ascending: false }).limit(24),
    ]);
    if (p.error) throw p.error;
    setProfile((p.data as MemberProfile | null) ?? null);
    setOrders((o.data as MemberOrder[] | null) ?? []);
    setViews((v.data as ProductView[] | null) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadData();
    if (!supabase) return;
    const { data } = supabase.auth.onAuthStateChange((_event, next) => { void loadData(next); });
    return () => data.subscription.unsubscribe();
  }, [loadData]);

  const sendSignInCode = async (email: string) => {
    const normalized = email.trim().toLowerCase();
    if (!supabase) {
      const p = demoProfile();
      if (!p || p.email.toLowerCase() !== normalized) throw new Error('MEMBER_NOT_FOUND');
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ mode: 'signin', email: normalized }));
      return;
    }
    const { error } = await supabase.auth.signInWithOtp({ email: normalized, options: { shouldCreateUser: false } });
    if (error) throw error;
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ mode: 'signin', email: normalized }));
  };

  const sendRegistrationCode = async (registration: Registration) => {
    const normalized = registration.email.trim().toLowerCase();
    const pending = { mode: 'register', ...registration, email: normalized };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: {
        shouldCreateUser: true,
        data: {
          full_name: registration.fullName.trim(), phone: registration.phone.trim(),
          birthday_month: String(registration.birthdayMonth), marketing_consent: registration.marketingConsent
        }
      }
    });
    if (error) throw error;
  };

  const verifyCode = async (email: string, token: string) => {
    const pending = readJson<Record<string, unknown> | null>(PENDING_KEY, null) ?? (() => {
      try { return JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch { return null; }
    })();
    if (!supabase) {
      if (token !== DEMO_CODE) throw new Error('INVALID_CODE');
      if (pending?.mode === 'register') {
        const existing = demoProfile();
        const now = new Date().toISOString();
        const next: MemberProfile = {
          id: existing?.id ?? crypto.randomUUID(), member_number: existing?.member_number ?? makeMemberNumber(),
          full_name: String(pending.fullName), phone: String(pending.phone), email: String(pending.email),
          birthday_month: Number(pending.birthdayMonth), marketing_consent: Boolean(pending.marketingConsent),
          created_at: existing?.created_at ?? now,
        };
        writeJson(PROFILE_KEY, next);
      }
      localStorage.setItem(SESSION_KEY, email.trim().toLowerCase());
      sessionStorage.removeItem(PENDING_KEY);
      await loadData();
      return;
    }
    const { error } = await supabase.auth.verifyOtp({ email: email.trim().toLowerCase(), token, type: 'email' });
    if (error) throw error;
    sessionStorage.removeItem(PENDING_KEY);
    await loadData();
  };

  const signOut = async () => {
    if (supabase) await supabase.auth.signOut();
    else { localStorage.removeItem(SESSION_KEY); setDemoEmail(null); }
    setProfile(null); setOrders([]); setViews([]);
  };

  const recordView: AuthValue['recordView'] = async (value) => {
    const view: ProductView = { ...value, viewed_at: value.viewed_at ?? new Date().toISOString() };
    const merged = [view, ...demoViews().filter(v => v.product_id !== view.product_id)].slice(0, 24);
    writeJson(VIEWS_KEY, merged);
    setViews(merged);
    if (!supabase || !session?.user) return;
    await supabase.from('product_views').upsert({
      member_id: session.user.id, product_id: view.product_id, title: view.title,
      image: view.image ?? null, url: view.url ?? null, viewed_at: view.viewed_at
    }, { onConflict: 'member_id,product_id' });
  };

  const isStaff = async () => {
    if (!supabase) return Boolean(profile);
    const { data, error } = await supabase.rpc('is_jaime_staff');
    if (error) return false;
    return Boolean(data);
  };

  const lookupMember = async (memberNumber: string) => {
    if (!supabase) {
      const p = demoProfile();
      return p?.member_number === memberNumber ? { profile: p, orders: demoOrders() } : null;
    }
    if (!(await isStaff())) return null;
    const p = await supabase.from('member_profiles').select('*').eq('member_number', memberNumber).maybeSingle();
    if (!p.data) return null;
    const o = await supabase.from('orders').select('*').eq('member_id', p.data.id).order('purchased_at', { ascending: false });
    return { profile: p.data as MemberProfile, orders: (o.data as MemberOrder[] | null) ?? [] };
  };

  const addOrder: AuthValue['addOrder'] = async (memberId, order) => {
    if (!supabase) {
      const list = demoOrders();
      const next: MemberOrder = { ...order, id: crypto.randomUUID(), member_id: memberId, status: 'completed' };
      writeJson(ORDERS_KEY, [next, ...list]);
      setOrders([next, ...list]);
      return;
    }
    const { error } = await supabase.from('orders').insert({ ...order, member_id: memberId, status: 'completed' });
    if (error) throw error;
  };

  const value = useMemo<AuthValue>(() => ({
    previewMode: !production, loading, sessionEmail: session?.user.email ?? demoEmail,
    profile, orders, views, sendSignInCode, sendRegistrationCode, verifyCode, signOut,
    refresh: () => loadData(), recordView, lookupMember, isStaff, addOrder,
  }), [loading, session, demoEmail, profile, orders, views, loadData]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
